/**
 * Country code → approximate capital city coordinates.
 * Used to position cyber events on the globe when lat/lng is missing.
 */

export const COUNTRY_COORDS: Record<string, [number, number]> = {
  AF: [34.5, 69.2], AL: [41.3, 19.8], DZ: [36.8, 3.1], AR: [-34.6, -58.4],
  AM: [40.2, 44.5], AU: [-33.9, 151.2], AT: [48.2, 16.4], AZ: [40.4, 49.9],
  BH: [26.2, 50.6], BD: [23.7, 90.4], BY: [53.9, 27.6], BE: [50.8, 4.4],
  BR: [-15.8, -47.9], BG: [42.7, 23.3], KH: [11.6, 104.9], CA: [45.4, -75.7],
  CL: [-33.4, -70.7], CN: [39.9, 116.4], CO: [4.7, -74.1], HR: [45.8, 16.0],
  CU: [23.1, -82.4], CY: [35.2, 33.4], CZ: [50.1, 14.4], DK: [55.7, 12.6],
  EC: [-0.2, -78.5], EG: [30.0, 31.2], EE: [59.4, 24.7], ET: [9.0, 38.7],
  FI: [60.2, 24.9], FR: [48.9, 2.3], GE: [41.7, 44.8], DE: [52.5, 13.4],
  GH: [5.6, -0.2], GR: [37.98, 23.7], HK: [22.3, 114.2], HU: [47.5, 19.0],
  IS: [64.1, -21.9], IN: [28.6, 77.2], ID: [-6.2, 106.8], IR: [35.7, 51.4],
  IQ: [33.3, 44.4], IE: [53.3, -6.3], IL: [31.8, 35.2], IT: [41.9, 12.5],
  JP: [35.7, 139.7], JO: [31.9, 35.9], KZ: [51.2, 71.4], KE: [-1.3, 36.8],
  KP: [39.0, 125.8], KR: [37.6, 127.0], KW: [29.4, 47.98], LV: [56.9, 24.1],
  LB: [33.9, 35.5], LY: [32.9, 13.2], LT: [54.7, 25.3], LU: [49.6, 6.1],
  MY: [3.1, 101.7], MV: [4.2, 73.5], MX: [19.4, -99.1], MD: [47.0, 28.8],
  MN: [47.9, 106.9], MA: [34.0, -6.8], MZ: [-25.97, 32.6], MM: [19.76, 96.1],
  NP: [27.7, 85.3], NL: [52.4, 4.9], NZ: [-41.3, 174.8], NG: [9.1, 7.5],
  NO: [59.9, 10.75], PK: [33.7, 73.0], PA: [9.0, -79.5], PY: [-25.3, -57.6],
  PE: [-12.0, -77.0], PH: [14.6, 121.0], PL: [52.2, 21.0], PT: [38.7, -9.1],
  QA: [25.3, 51.5], RO: [44.4, 26.1], RU: [55.8, 37.6], SA: [24.7, 46.7],
  SG: [1.3, 103.8], SK: [48.1, 17.1], SI: [46.1, 14.5], ZA: [-25.7, 28.2],
  ES: [40.4, -3.7], LK: [6.9, 79.9], SE: [59.3, 18.1], CH: [46.9, 7.4],
  SY: [33.5, 36.3], TW: [25.0, 121.5], TH: [13.8, 100.5], TR: [39.9, 32.9],
  UA: [50.4, 30.5], AE: [24.5, 54.7], GB: [51.5, -0.1], US: [38.9, -77.0],
  UY: [-34.9, -56.2], UZ: [41.3, 69.3], VE: [10.5, -66.9], VN: [21.0, 105.8],
  YE: [15.4, 44.2], ZM: [-15.4, 28.3], ZW: [-17.8, 31.0],
  VI: [18.3, -64.9], ME: [42.4, 19.3],
}

const ATTACKER_ORIGINS: [number, number][] = [
  [55.8, 37.6],   // Moscow
  [39.9, 116.4],  // Beijing
  [39.0, 125.8],  // Pyongyang
  [35.7, 51.4],   // Tehran
  [52.5, 13.4],   // Berlin (proxy)
  [37.6, 127.0],  // Seoul (proxy)
  [-33.9, 151.2], // Sydney (proxy)
  [38.9, -77.0],  // Washington (proxy)
]

/** Normalize country codes so USA, UK, etc. resolve to same coords as US, GB */
const COUNTRY_ALIASES: Record<string, string> = {
  USA: 'US',
  'UNITED STATES': 'US',
  'U.S.': 'US',
  'U.S.A.': 'US',
  UK: 'GB',
  'UNITED KINGDOM': 'GB',
  GBR: 'GB',
  UAE: 'AE',
  'UNITED ARAB EMIRATES': 'AE',
  RUS: 'RU',
  RUSSIA: 'RU',
  DEU: 'DE',
  FRA: 'FR',
  IRN: 'IR',
  IRQ: 'IQ',
  PHL: 'PH',
  VNM: 'VN',
  KOR: 'KR',
  PRK: 'KP',
}

export function getCountryLatLng(code: string | null | undefined): [number, number] | null {
  if (!code) return null
  const raw = code.toString().trim().toUpperCase()
  const key = raw.length > 2 ? COUNTRY_ALIASES[raw] ?? raw.slice(0, 2) : raw
  const twoLetter = (COUNTRY_ALIASES[key] ?? key).slice(0, 2)
  return COUNTRY_COORDS[twoLetter] ?? null
}

let _originIdx = 0
export function getRandomAttackerOrigin(): [number, number] {
  const origin = ATTACKER_ORIGINS[_originIdx % ATTACKER_ORIGINS.length]
  _originIdx++
  return [origin[0] + (Math.random() - 0.5) * 6, origin[1] + (Math.random() - 0.5) * 6]
}

/** Rough nearest-country lookup for a given lat/lng, based on COUNTRY_COORDS centroids. */
export function getNearestCountryCode(lat: number, lng: number): string | null {
  let bestCode: string | null = null
  let bestDist = Infinity
  for (const [code, [clat, clng]] of Object.entries(COUNTRY_COORDS)) {
    const dLat = lat - clat
    const dLng = lng - clng
    const dist = dLat * dLat + dLng * dLng
    if (dist < bestDist) {
      bestDist = dist
      bestCode = code
    }
  }
  // Only accept if we're reasonably close (within ~25 degrees squared)
  return bestDist <= 25 * 25 ? bestCode : null
}
